import BarChartIcon from "@redotech/redo-icons/bar-chart-01.svg";
import { memo, type ReactNode, useMemo } from "react";
import { ChartCard } from "../../arbiter-components/charts/chart-card/chart-card";
import {
  InsightsBreakdownDatum,
  InsightsBreakdownGraph,
} from "../../arbiter-components/charts/insights-breakdown/insights-breakdown-graph";
import { RedoLineAndBarChart } from "../../arbiter-components/charts/line-and-bar-chart/redo-line-and-bar-chart";
import type { ChartSeries } from "../../arbiter-components/charts/line-and-bar-chart/types";
import { RedoEmptyState } from "../../arbiter-components/empty-state/redo-empty-state";
import { Flex } from "../../flex";
import { LoadingRedoAnimation } from "../../loading-redo-animation";
import { AnalyticsSection } from "./analytics-section";
import { ShopperAgentInsightsData } from "./shopper-agent-insights-data";

const SENTIMENT_COLORS: Record<string, string> = {
  positive: "#17B26A",
  neutral: "#E5E5E5",
  negative: "#F04438",
};

/** Humanizes an engine label ("ai_only" → "Ai only"); null/empty → "Unknown". */
function prettyLabel(label: string | null): string {
  if (!label) {
    return "Unknown";
  }
  const spaced = label.replace(/_/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function buildTrend(
  timeSeries: ShopperAgentInsightsData["timeSeries"] | undefined,
): ChartSeries[] {
  if (!timeSeries?.length) {
    return [];
  }
  return [
    {
      name: "Conversations",
      data: timeSeries.flatMap((item) => {
        const parsed = new Date(`${item.date}T00:00:00.000Z`);
        if (Number.isNaN(parsed.getTime())) {
          return [];
        }
        return [
          {
            date: Temporal.Instant.from(parsed.toISOString()),
            value: item.count,
          },
        ];
      }),
    },
  ];
}

/**
 * Presentational "Sentiment & topics" section for a conversation surface.
 * Owns no fetching — the caller passes the shopperAgentInsightsAnalytics result.
 */
export const ShopperAgentInsightsSection = memo(
  function ShopperAgentInsightsSection({
    data,
    pending,
  }: {
    data: ShopperAgentInsightsData | undefined;
    pending: boolean;
  }) {
    const sentiment = useMemo<InsightsBreakdownDatum[]>(
      () =>
        (data?.sentiment ?? []).map((row) => ({
          label: prettyLabel(row.label),
          value: row.count,
          color: row.label
            ? SENTIMENT_COLORS[row.label.toLowerCase()]
            : undefined,
        })),
      [data?.sentiment],
    );
    const topics = useMemo<InsightsBreakdownDatum[]>(
      () =>
        (data?.topics ?? []).map((row) => ({
          label: row.topic,
          value: row.count,
        })),
      [data?.topics],
    );
    const handledBy = useMemo<InsightsBreakdownDatum[]>(
      () =>
        (data?.handledBy ?? []).map((row) => ({
          label: prettyLabel(row.label),
          value: row.count,
        })),
      [data?.handledBy],
    );
    const trend = useMemo(
      () => buildTrend(data?.timeSeries),
      [data?.timeSeries],
    );

    if (!pending && (data?.totalConversations ?? 0) === 0) {
      return (
        <AnalyticsSection title="Sentiment & topics">
          <Flex align="center" justify="center" p="3xl" w="full">
            <RedoEmptyState
              Icon={BarChartIcon}
              subtext="Sentiment and topics appear here once conversations have been analyzed."
              text="No analyzed conversations in this period"
            />
          </Flex>
        </AnalyticsSection>
      );
    }

    return (
      <AnalyticsSection title="Sentiment & topics">
        <ChartCard title="Sentiment" variant="single">
          <CardBody pending={pending}>
            <InsightsBreakdownGraph totals={sentiment} />
          </CardBody>
        </ChartCard>
        <ChartCard title="Top topics" variant="single">
          <CardBody pending={pending}>
            <InsightsBreakdownGraph totals={topics} />
          </CardBody>
        </ChartCard>
        <ChartCard title="Handled by" variant="single">
          <CardBody pending={pending}>
            <InsightsBreakdownGraph totals={handledBy} />
          </CardBody>
        </ChartCard>
        <ChartCard title="Conversations analyzed over time" variant="single">
          <CardBody pending={pending}>
            <RedoLineAndBarChart
              chartStyle="line"
              legendPosition="bottom"
              series={trend}
              showTooltipSeriesNames
              valueAxisFormatter={(value) => `${Math.round(value)}`}
              valueTooltipFormatter={(value) => `${Math.round(value)}`}
            />
          </CardBody>
        </ChartCard>
      </AnalyticsSection>
    );
  },
);

const CardBody = memo(function CardBody({
  pending,
  children,
}: {
  pending: boolean;
  children: ReactNode;
}) {
  if (pending) {
    return (
      <Flex align="center" justify="center" w="full">
        <LoadingRedoAnimation />
      </Flex>
    );
  }
  return <>{children}</>;
});
